import { generateSaturatedColor } from '../../Utils/colorUtils';

const DEFAULT_TAG_COLOR = '#5757b0';
const TAG_BOX_PADDING = 0.008;
export const VIEWER_TAG_HOVER_EVENT = 'dataunfold:viewer-tag-hover';

const clamp01 = (value) => Math.max(0, Math.min(1, Number(value)));

const normalizeComparableValue = (value) => (
  value === null || value === undefined ? '' : String(value).trim()
);

const normalizeComparableVariable = (value) => normalizeComparableValue(value).toLowerCase();

export const buildTagHoverEventDetail = (tag, timeevent = null, occurrenceIndex = null) => {
  if (!tag) {
    return null;
  }

  return {
    eventId: tag?.timeeventid ?? tag?.timeeventId ?? timeevent?.id ?? timeevent?.timeeventid ?? null,
    tagId: tag?.id ?? null,
    tagVariable: tag?.variable || '',
    occurrenceIndex: Number.isInteger(occurrenceIndex) && occurrenceIndex >= 0 ? occurrenceIndex : null,
  };
};

export const doesTagHoverDetailMatch = (detail, tag, timeevent = null) => {
  if (!detail || !tag) {
    return false;
  }

  const detailEventId = normalizeComparableValue(detail.eventId);
  const tagEventId = normalizeComparableValue(
    timeevent?.id ?? timeevent?.timeeventid ?? tag?.timeeventid ?? tag?.timeeventId
  );

  if (!detailEventId || !tagEventId || detailEventId !== tagEventId) {
    return false;
  }

  const detailTagId = normalizeComparableValue(detail.tagId);
  const tagId = normalizeComparableValue(tag?.id);
  if (detailTagId && tagId && detailTagId === tagId) {
    return true;
  }

  const detailTagVariable = normalizeComparableVariable(detail.tagVariable);
  const tagVariable = normalizeComparableVariable(tag?.variable);
  return Boolean(detailTagVariable && tagVariable && detailTagVariable === tagVariable);
};

const hslToRgba = (color, alpha) => {
  const match = String(color || '').match(/hsl\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%\s*\)/i);
  if (!match) {
    return `rgba(87, 87, 176, ${alpha})`;
  }

  let h = Number(match[1]) % 360;
  if (h < 0) h += 360;
  const s = Number(match[2]) / 100;
  const l = Number(match[3]) / 100;
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - chroma / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) [r, g, b] = [chroma, x, 0];
  else if (h < 120) [r, g, b] = [x, chroma, 0];
  else if (h < 180) [r, g, b] = [0, chroma, x];
  else if (h < 240) [r, g, b] = [0, x, chroma];
  else if (h < 300) [r, g, b] = [x, 0, chroma];
  else [r, g, b] = [chroma, 0, x];

  return `rgba(${Math.round((r + m) * 255)}, ${Math.round((g + m) * 255)}, ${Math.round((b + m) * 255)}, ${alpha})`;
};

export const getTagHighlightColor = (label) => (
  label ? generateSaturatedColor(String(label)) : DEFAULT_TAG_COLOR
);

export const getTagHighlightBackgroundColor = (label, alpha = 0.12) => (
  hslToRgba(getTagHighlightColor(label), alpha)
);

export const padNormalizedBbox = (bbox, padding = TAG_BOX_PADDING) => {
  if (!Array.isArray(bbox) || bbox.length !== 4) {
    return null;
  }

  const [x0, y0, x1, y1] = bbox.map(clamp01);
  return [
    clamp01(x0 - padding),
    clamp01(y0 - padding),
    clamp01(x1 + padding),
    clamp01(y1 + padding),
  ];
};

export const rotateNormalizedBbox = (bbox, rotationDegrees = 0) => {
  if (!Array.isArray(bbox) || bbox.length !== 4) {
    return null;
  }

  const normalizedRotation = ((Number(rotationDegrees) || 0) % 360 + 360) % 360;
  const [x0, y0, x1, y1] = bbox.map(clamp01);
  const corners = [
    [x0, y0],
    [x1, y0],
    [x1, y1],
    [x0, y1],
  ];

  let mappedCorners;
  if (normalizedRotation === 90) {
    mappedCorners = corners.map(([x, y]) => [1 - y, x]);
  } else if (normalizedRotation === 180) {
    mappedCorners = corners.map(([x, y]) => [1 - x, 1 - y]);
  } else if (normalizedRotation === 270) {
    mappedCorners = corners.map(([x, y]) => [y, 1 - x]);
  } else {
    mappedCorners = corners;
  }

  const xs = mappedCorners.map(([x]) => clamp01(x));
  const ys = mappedCorners.map(([, y]) => clamp01(y));
  return [
    Math.min(...xs),
    Math.min(...ys),
    Math.max(...xs),
    Math.max(...ys),
  ];
};
